const VK_ID_TOKEN_URL = 'https://id.vk.com/oauth2/auth';
const VK_CALLBACK_PATH = '/auth/vk-callback';

const VK_STATE_KEY = 'vk_state';
const VK_CODE_VERIFIER_KEY = 'vk_code_verifier';
const VK_DEVICE_ID_KEY = 'vk_device_id';
const VK_PKCE_STORED_AT_KEY = 'vk_pkce_stored_at';
const VK_OAUTH_FLOW_MODE_KEY = 'vk_oauth_flow_mode';
const VK_OAUTH_LINK_RETURN_URL_KEY = 'vk_oauth_link_return_url';
const VK_OAUTH_FLOW_MODE_AUTH = 'auth';
const VK_OAUTH_FLOW_MODE_LINK = 'link';

/** Max age for PKCE values kept in localStorage (cross-tab); reject stale flows on callback. */
export const VK_OAUTH_PKCE_TTL_MS = 15 * 60 * 1000;

export function clearVkOAuthPkceSession() {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.removeItem(VK_STATE_KEY);
  localStorage.removeItem(VK_CODE_VERIFIER_KEY);
  localStorage.removeItem(VK_DEVICE_ID_KEY);
  localStorage.removeItem(VK_PKCE_STORED_AT_KEY);
}

export function writeVkOAuthPkceSession(state, codeVerifier, deviceId) {
  if (typeof localStorage === 'undefined') {
    return;
  }
  clearVkOAuthPkceSession();
  localStorage.setItem(VK_STATE_KEY, state);
  localStorage.setItem(VK_CODE_VERIFIER_KEY, codeVerifier);
  localStorage.setItem(VK_DEVICE_ID_KEY, deviceId);
  localStorage.setItem(VK_PKCE_STORED_AT_KEY, String(Date.now()));
}

export function clearVkOAuthFlowMode() {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.removeItem(VK_OAUTH_FLOW_MODE_KEY);
}

export function writeVkOAuthLinkReturnUrl(url) {
  if (typeof localStorage === 'undefined') {
    return;
  }
  if (!url) {
    localStorage.removeItem(VK_OAUTH_LINK_RETURN_URL_KEY);
    return;
  }
  localStorage.setItem(VK_OAUTH_LINK_RETURN_URL_KEY, url);
}

export function readVkOAuthLinkReturnUrl() {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  return localStorage.getItem(VK_OAUTH_LINK_RETURN_URL_KEY);
}

export function consumeVkOAuthLinkReturnUrl() {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  const value = localStorage.getItem(VK_OAUTH_LINK_RETURN_URL_KEY);
  localStorage.removeItem(VK_OAUTH_LINK_RETURN_URL_KEY);
  return value;
}

export function markVkOAuthFlowAsAuth() {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.setItem(VK_OAUTH_FLOW_MODE_KEY, VK_OAUTH_FLOW_MODE_AUTH);
}

export function markVkOAuthFlowAsLink() {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.setItem(VK_OAUTH_FLOW_MODE_KEY, VK_OAUTH_FLOW_MODE_LINK);
}

export function isVkOAuthLinkFlow() {
  if (typeof localStorage === 'undefined') {
    return false;
  }
  return localStorage.getItem(VK_OAUTH_FLOW_MODE_KEY) === VK_OAUTH_FLOW_MODE_LINK;
}

/**
 * Returns PKCE payload if present and within TTL; otherwise clears keys and returns null.
 */
export function readVkOAuthPkceSession() {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  const state = localStorage.getItem(VK_STATE_KEY);
  const codeVerifier = localStorage.getItem(VK_CODE_VERIFIER_KEY);
  const deviceId = localStorage.getItem(VK_DEVICE_ID_KEY);
  const storedAtRaw = localStorage.getItem(VK_PKCE_STORED_AT_KEY);

  const hasAny = !!(state || codeVerifier || deviceId || storedAtRaw);
  if (!state || !codeVerifier) {
    if (hasAny) {
      clearVkOAuthPkceSession();
    }
    return null;
  }

  if (!storedAtRaw) {
    clearVkOAuthPkceSession();
    return null;
  }

  const storedAt = Number(storedAtRaw);
  const age = Date.now() - storedAt;
  if (!Number.isFinite(age) || age < 0 || age > VK_OAUTH_PKCE_TTL_MS) {
    clearVkOAuthPkceSession();
    return null;
  }
  return { state, codeVerifier, deviceId: deviceId || '' };
}

export function getVkOAuthRedirectOrigin(apiConfig) {
  const fromConfig = (apiConfig?.originUrl || '').replace(/\/$/, '');
  const fromWindow = typeof window !== 'undefined' ? window.location.origin : '';
  if (fromConfig && fromWindow && fromConfig === fromWindow) {
    return fromConfig;
  }
  if (fromWindow) {
    return fromWindow;
  }
  return fromConfig;
}

export function buildVkIdRedirectUri(apiConfig) {
  return `${getVkOAuthRedirectOrigin(apiConfig)}${VK_CALLBACK_PATH}`;
}

export function getVkIdTokenExchangeUrl() {
  return VK_ID_TOKEN_URL;
}

export function extractVkIdAuthToken(data) {
  const d = data?.data;
  return (
    d?.token ||
    d?.accessToken ||
    d?.access_token ||
    data?.token ||
    data?.accessToken ||
    data?.access_token ||
    null
  );
}

export function replaceLocationWithVkPanelToken(token) {
  const lang =
    (typeof localStorage !== 'undefined' && localStorage.getItem('currentLangId')) || 'ru';
  window.location.replace(`/${lang}/my/?token=${encodeURIComponent(token)}`);
}

export function isVkOAuthCallbackWithParams(pathname, searchParams) {
  const path = pathname || '';
  const onRoute = path === VK_CALLBACK_PATH || path.endsWith(VK_CALLBACK_PATH);
  if (!onRoute || !searchParams) {
    return false;
  }
  return searchParams.has('code') || searchParams.has('error');
}

function generateRandomString(length) {
  const CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
  const randomValues = new Uint8Array(length);
  window.crypto.getRandomValues(randomValues);

  let result = '';
  for (let i = 0; i < length; i++) {
    result += CHARSET[randomValues[i] % CHARSET.length];
  }
  return result;
}

async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digestBuffer = await window.crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode.apply(null, new Uint8Array(digestBuffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export async function startVkIdLinkingFlow({
  socialNetworkName,
  apiConfig,
  lastLinkedSocialNetworkKey,
}) {
  markVkOAuthFlowAsLink();
  if (typeof window !== 'undefined') {
    writeVkOAuthLinkReturnUrl(
      `${window.location.pathname}${window.location.search}${window.location.hash || ''}`
    );
  }
  const state = generateRandomString(32);
  const codeVerifier = generateRandomString(128);
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const deviceId = generateRandomString(32);

  writeVkOAuthPkceSession(state, codeVerifier, deviceId);
  if (typeof localStorage !== 'undefined' && lastLinkedSocialNetworkKey) {
    localStorage.setItem(lastLinkedSocialNetworkKey, socialNetworkName);
  }

  const authParams = new URLSearchParams({
    response_type: 'code',
    client_id: String(apiConfig.appId),
    redirect_uri: buildVkIdRedirectUri(apiConfig),
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    device_id: deviceId,
  });

  window.location.assign(`https://id.vk.ru/authorize?${authParams.toString()}`);
}

